// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit } from '@evolvemep/api-ts';
import { Checkbox, type CheckboxProps, Group, Loader, type LoaderProps, Text, type TextProps } from '@mantine/core';

type Props = StrongOmit<CheckboxProps, 'value' | 'onChange' | 'checked'> & {
  value?: boolean | null;
  onChange: (value: boolean) => void;
  /** Replaces the checkbox with a loader beside its label while the data it acts on is loading. */
  loading?: boolean;
  loaderSize?: LoaderProps['size'];
  textSize?: TextProps['fz'];
};

/**
 * A `Checkbox` with a typed boolean `value`/`onChange` that can show a loading state in place of the box.
 * The default checkbox size is equivalent to default loader sizes set as default values
 */
export const WrappedCheckbox = ({
  value,
  onChange,
  loading,
  size,
  label,
  loaderSize = 'xs',
  textSize = 'sm',
  ...props
}: Props) =>
  loading ? (
    <Group gap="sm" align="center" wrap="nowrap">
      {/* the default checkbox size is equivalent to loader xs and text sm */}
      <Loader size={loaderSize} />
      <Text fz={textSize}>{label}</Text>
    </Group>
  ) : (
    <Checkbox
      {...props}
      size={size}
      label={label}
      checked={value ?? false}
      onChange={(e) => onChange(e.currentTarget.checked)}
    />
  );
